/**
 * Complete-step copy. Shared end screen is Figma 3338:20896; path intros remain
 * from briefs 2940:12085 / 2940:12095 for any transcript that still reads them.
 */

import {
	EQuestionnairePath,
	EStepSelection,
	type IQuestionnaireAnswers,
	type IQuestionnaireOption,
	QUESTIONNAIRE_FLOWS,
	type TCtaHrefKey,
	type TQuestionnairePath,
} from "./questionnaire-flow";

export const QUESTIONNAIRE_COMPLETE_CTA_LABEL = "Get started";

export type TQuestionnaireCompletePresentation = {
	heading: string;
	subtitle: string;
	ctaLabel: string;
};

/** End card copy follows the path the buyer chose — funding, payments, or cash flow. */
export function resolveCompletePresentation(path: TQuestionnairePath): TQuestionnaireCompletePresentation {
	switch (path) {
		case EQuestionnairePath.FUND:
			return {
				heading: "Fund the stock. Pay us back as it sells.",
				subtitle:
					"A revolving facility for inventory, supplier bills, and getting paid early on invoices you are already owed.",
				ctaLabel: "Get funding",
			};
		case EQuestionnairePath.PAYMENTS:
			return {
				heading: "Pay suppliers in one click",
				subtitle: "Build a pay run from the bills you already have, and send it from cash or from Triffin Credit.",
				ctaLabel: QUESTIONNAIRE_COMPLETE_CTA_LABEL,
			};
		case EQuestionnairePath.CASH_FLOW:
			return {
				heading: "See the next 13 weeks of cash",
				subtitle: "Connect your accounts and decide who to pay, what to buy, and what you can afford to grow.",
				ctaLabel: QUESTIONNAIRE_COMPLETE_CTA_LABEL,
			};
	}
}

export function resolveCompleteCtaHrefKey(path: TQuestionnairePath): TCtaHrefKey {
	const step = QUESTIONNAIRE_FLOWS[path].find((item) => item.selection === EStepSelection.COMPLETE);
	return step?.ctaHrefKey ?? "getStarted";
}

function optionLabel(options: readonly IQuestionnaireOption[], id: string | string[] | undefined): string | null {
	if (typeof id !== "string") return null;
	return options.find((option) => option.id === id)?.label ?? null;
}

const PAY_TODAY_OPTIONS: readonly IQuestionnaireOption[] = [
	{ id: "one_by_one", label: "one transfer at a time" },
	{ id: "accounting_run", label: "an accounting pay run" },
	{ id: "bank_portal", label: "a bank portal" },
	{ id: "depends", label: "a mix of methods" },
];

const PAY_RUN_PAIN_OPTIONS: readonly IQuestionnaireOption[] = [
	{ id: "admin", label: "cut the manual admin" },
	{ id: "cash_tight", label: "pay on time even when cash is tight" },
	{ id: "priority", label: "see who to pay first" },
	{ id: "errors", label: "stop remittance errors" },
];

const CASH_PICTURE_OPTIONS: readonly IQuestionnaireOption[] = [
	{ id: "spreadsheets", label: "spreadsheets" },
	{ id: "bank_balance", label: "the bank balance" },
	{ id: "accounting", label: "accounting software" },
	{ id: "no_view", label: "no single view" },
];

const CASH_DECISION_OUTCOMES: Record<string, string> = {
	stock_buy: "know if the next stock buy fits",
	which_bills: "choose which bills to pay this week",
	marketing: "see what marketing you can afford",
	surprises: "stop being surprised by the balance",
};

/** Payments close — how they pay today, the pain, and monthly volume. */
export function buildPaymentsCompleteIntro(answers: IQuestionnaireAnswers): string {
	const today = optionLabel(PAY_TODAY_OPTIONS, answers.payToday) ?? "your current process";
	const pain = optionLabel(PAY_RUN_PAIN_OPTIONS, answers.payRunPain) ?? "streamline the pay run";
	const volume = optionLabel(
		QUESTIONNAIRE_FLOWS[EQuestionnairePath.PAYMENTS].find((step) => step.id === "paymentVolume")?.options ?? [],
		answers.paymentVolume,
	);
	const volumeClause = volume ? ` on ${volume} a month` : "";
	return `Based on paying suppliers with ${today}${volumeClause}, Triffin payments could help you ${pain}.`;
}

/** Cash-flow close — where the picture lives, and the decision they need. */
export function buildCashFlowCompleteIntro(answers: IQuestionnaireAnswers): string {
	const picture = optionLabel(CASH_PICTURE_OPTIONS, answers.cashPicture) ?? "your current process";
	const decisionId = typeof answers.cashDecision === "string" ? answers.cashDecision : null;
	const outcome = (decisionId && CASH_DECISION_OUTCOMES[decisionId]) || "see the next 13 weeks clearly";
	return `Based on tracking cash in ${picture}, Triffin could help you ${outcome}.`;
}

export function resolveCompleteIntro(
	path: TQuestionnairePath,
	answers: IQuestionnaireAnswers,
	fallback?: string,
): string {
	if (path === EQuestionnairePath.PAYMENTS) {
		return buildPaymentsCompleteIntro(answers);
	}
	if (path === EQuestionnairePath.CASH_FLOW) {
		return buildCashFlowCompleteIntro(answers);
	}
	return fallback ?? "";
}
